/**
 * Health review helpers ("Befund zur Kenntnis nehmen").
 *
 * Phase 0 of the health-findings loop
 * (docs/plans/2026-09-03-befund-schleife.md). An acknowledgement used to write
 * `HealthReviewStatus: "acknowledged"` and nothing ever read it back, so the
 * animal stayed in "Attention now" as if nothing had happened.
 *
 * The review now stores a snapshot of the Watchdog evidence it was given for
 * (severity, reason codes, score). The attention selectors treat the review as
 * *current* only while that evidence is unchanged:
 *   - the severity has not climbed above the reviewed severity,
 *   - no reason code the farmer has not seen yet has appeared,
 *   - the health score has not dropped clearly below the reviewed score,
 *   - the anomaly run the review was given for has not broken since,
 *   - the review is younger than the shared "recently handled" window.
 * Any of these brings the animal back into the list. Phase 1 replaces the
 * snapshot with a proper finding document; the field names stay.
 *
 * The anomaly-run check is what catches a *new episode*: the other checks all
 * compare against the reviewed snapshot, so an animal that recovered and then
 * relapsed with the same reason codes — or one acknowledged at "critical",
 * which nothing can climb above — would otherwise stay hidden for the whole
 * window while it is sick again.
 *
 * NOTE: the mobile app mirrors the read contract in `src/utils/animalHealth.ts`.
 * Keep the field names and the rules above in sync when changing them.
 */

import { getAnimalHealthScore } from "./animalUtils";
import { getTimestampInMillis } from "./epochMillis";

/** Status value written by an acknowledgement. Legacy "dismissed" never counts. */
export const HEALTH_REVIEW_STATUS_ACKNOWLEDGED = "acknowledged";

/** A review expires after the same window the "recently handled" rules use. */
export const HEALTH_REVIEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Score points below the reviewed score that count as an escalation. */
export const HEALTH_REVIEW_SCORE_DROP = 15;

const SEVERITY_RANK = { none: 0, low: 1, medium: 2, high: 3, critical: 4 };

/** Reason codes that describe data quality, not the animal — never an escalation. */
const INFORMATIONAL_REASONS = new Set(["insufficient_data"]);

/**
 * Normalizes a raw HealthSeverity value onto the known enum.
 * Unknown or missing values resolve to "none".
 * @param {unknown} value - Raw severity
 * @returns {"none"|"low"|"medium"|"high"|"critical"} Normalized severity
 */
export function normalizeHealthSeverity(value) {
	if (typeof value !== "string") return "none";
	// Compound values such as "high activity_sensor" carry the severity first.
	const token = value.trim().toLowerCase().split(/\s+/)[0];
	return Object.prototype.hasOwnProperty.call(SEVERITY_RANK, token) ? token : "none";
}

/**
 * Normalizes a list of reason codes: lowercase, hyphens folded to underscores,
 * informational codes dropped, duplicates removed, sorted for stable storage.
 * @param {unknown} list - Raw HealthReasons value
 * @returns {string[]} Normalized reason codes
 */
export function normalizeHealthReasons(list) {
	if (!Array.isArray(list)) return [];
	const codes = list
		.filter((code) => typeof code === "string" && code.trim() !== "")
		.map((code) => code.trim().toLowerCase().replace(/-/g, "_"))
		.filter((code) => !INFORMATIONAL_REASONS.has(code));
	return Array.from(new Set(codes)).sort();
}

function toMillis(value) {
	return getTimestampInMillis(value);
}

/**
 * Normalizes the Watchdog's consecutive-anomaly counter.
 * Missing or malformed values resolve to null, which disables the comparison
 * instead of guessing.
 * @param {unknown} value - Raw ConsecutiveAnomalyRuns value
 * @returns {number|null} Non-negative run count, or null when unknown
 */
export function normalizeAnomalyRuns(value) {
	const runs = typeof value === "string" ? Number(value) : value;
	if (typeof runs !== "number" || !Number.isFinite(runs) || runs < 0) return null;
	return Math.floor(runs);
}

/**
 * Tri-state confirmation of a Watchdog finding.
 *
 * `ConfirmationMet` is only written by Watchdog v3 (see
 * docs/plans/2026-09-03-befund-schleife.md). On every document written before
 * that the field is absent, which means "unknown" — not "not confirmed".
 * Reading the absence as the latter labels a confirmed critical finding as a
 * harmless first hint, so callers must handle null by showing nothing.
 * @param {Object} animal - Animal document
 * @returns {boolean|null} Confirmation state, or null when the Watchdog reported none
 */
export function getFindingConfirmation(animal) {
	return typeof animal?.ConfirmationMet === "boolean" ? animal.ConfirmationMet : null;
}

/**
 * Builds the Firestore update written by an acknowledgement.
 * @param {Object} animal - Animal document as loaded from Firestore
 * @param {Object} [options]
 * @param {number} [options.reviewedAt] - Review time in epoch ms (default: now)
 * @param {string|null} [options.reviewedBy] - Reviewer user id
 * @returns {Object} Update payload for `updateAnimalFields`
 */
export function buildHealthReviewUpdate(animal, { reviewedAt = Date.now(), reviewedBy = null } = {}) {
	return {
		HealthReviewedAt: reviewedAt,
		HealthReviewedBy: reviewedBy,
		HealthReviewStatus: HEALTH_REVIEW_STATUS_ACKNOWLEDGED,
		HealthReviewedSeverity: normalizeHealthSeverity(animal?.HealthSeverity),
		HealthReviewedReasons: normalizeHealthReasons(animal?.HealthReasons),
		HealthReviewedScore: getAnimalHealthScore(animal),
		HealthReviewedEvaluatedAt: toMillis(animal?.HealthEvaluatedAt),
		HealthReviewedAnomalyRuns: normalizeAnomalyRuns(animal?.ConsecutiveAnomalyRuns),
	};
}

/**
 * @typedef {Object} HealthReviewState
 * @property {boolean} reviewed - An acknowledgement exists
 * @property {number|null} reviewedAt - Review time in epoch ms
 * @property {boolean} hasSnapshot - The review carries the evidence snapshot
 * @property {boolean} expired - The review is older than the window
 * @property {boolean} severityEscalated - Severity climbed above the reviewed one
 * @property {string[]} newReasons - Reason codes the farmer has not seen yet
 * @property {boolean} scoreDropped - Score fell clearly below the reviewed score
 * @property {boolean} episodeRestarted - The reviewed anomaly run broke and a new one began
 * @property {boolean} current - The review still covers today's evidence
 */

/**
 * Resolves whether an acknowledgement still covers the animal's current evidence.
 * @param {Object} animal - Animal document
 * @param {Object} [options]
 * @param {number} [options.nowMs] - Reference time (default: now)
 * @param {number} [options.windowMs] - Review validity window
 * @returns {HealthReviewState} Review state
 */
export function getHealthReviewState(
	animal,
	{ nowMs = Date.now(), windowMs = HEALTH_REVIEW_WINDOW_MS } = {}
) {
	const state = {
		reviewed: false,
		reviewedAt: null,
		hasSnapshot: false,
		expired: false,
		severityEscalated: false,
		newReasons: [],
		scoreDropped: false,
		episodeRestarted: false,
		current: false,
	};

	if (!animal || typeof animal !== "object") return state;

	const reviewedAt = toMillis(animal.HealthReviewedAt);
	const status =
		typeof animal.HealthReviewStatus === "string"
			? animal.HealthReviewStatus.trim().toLowerCase()
			: "";
	if (reviewedAt === null || status !== HEALTH_REVIEW_STATUS_ACKNOWLEDGED) {
		return state;
	}

	state.reviewed = true;
	state.reviewedAt = reviewedAt;

	// Reviews written before the snapshot existed cannot be compared — they keep
	// their old (ineffective) behaviour instead of silently hiding an animal.
	state.hasSnapshot = typeof animal.HealthReviewedSeverity === "string";
	if (!state.hasSnapshot) return state;

	state.expired = nowMs - reviewedAt > windowMs;

	const currentSeverity = normalizeHealthSeverity(animal.HealthSeverity);
	const reviewedSeverity = normalizeHealthSeverity(animal.HealthReviewedSeverity);
	state.severityEscalated = SEVERITY_RANK[currentSeverity] > SEVERITY_RANK[reviewedSeverity];

	const reviewedReasons = new Set(normalizeHealthReasons(animal.HealthReviewedReasons));
	state.newReasons = normalizeHealthReasons(animal.HealthReasons).filter(
		(code) => !reviewedReasons.has(code)
	);

	const currentScore = getAnimalHealthScore(animal);
	const reviewedScore =
		typeof animal.HealthReviewedScore === "number" && Number.isFinite(animal.HealthReviewedScore)
			? animal.HealthReviewedScore
			: null;
	state.scoreDropped =
		currentScore !== null &&
		reviewedScore !== null &&
		reviewedScore - currentScore > HEALTH_REVIEW_SCORE_DROP;

	// Within one episode the Watchdog's run counter only climbs. A lower value
	// therefore means the run the farmer acknowledged has ended and the animal
	// is flagged again for a fresh reason — the review does not cover that.
	// Both values must be known; otherwise the comparison is skipped rather
	// than guessed (farms whose Watchdog predates v3 keep the old behaviour).
	const currentRuns = normalizeAnomalyRuns(animal.ConsecutiveAnomalyRuns);
	const reviewedRuns = normalizeAnomalyRuns(animal.HealthReviewedAnomalyRuns);
	state.episodeRestarted =
		currentRuns !== null && reviewedRuns !== null && currentRuns < reviewedRuns;

	state.current =
		!state.expired &&
		!state.severityEscalated &&
		state.newReasons.length === 0 &&
		!state.scoreDropped &&
		!state.episodeRestarted;

	return state;
}

/**
 * True while an acknowledgement still covers the animal's current evidence.
 * @param {Object} animal - Animal document
 * @param {Object} [options] - See `getHealthReviewState`
 * @returns {boolean} Whether the review is current
 */
export function isHealthReviewCurrent(animal, options) {
	return getHealthReviewState(animal, options).current;
}
